import { useEffect, useRef, useState } from 'react'
import { getSocket } from '../../socket/client.js'

const AUTO_CLEAR_MS = 8000

interface TypingEvent {
  channel: string
  chatId: string
  actor: 'ai' | 'agent'
  actorName: string
  actorUserId: string | null
  state: 'start' | 'stop'
}

/** Who is currently typing in this chat (AI and/or other agents). Self-clears after 8s without a refresh. */
export function useChannelTyping(channel: string, chatId: string | null): { actors: Array<{ key: string; name: string }> } {
  const [actors, setActors] = useState<Array<{ key: string; name: string }>>([])
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>())

  useEffect(() => {
    setActors([])
    if (!chatId) return
    const socket = getSocket()
    const onTyping = (e: TypingEvent): void => {
      if (e.channel !== channel || e.chatId !== chatId) return
      const key = e.actor === 'ai' ? 'ai' : `agent:${e.actorUserId ?? e.actorName}`
      const clear = (): void => {
        const t = timers.current.get(key)
        if (t) clearTimeout(t)
        timers.current.delete(key)
        setActors((prev) => prev.filter((a) => a.key !== key))
      }
      if (e.state === 'stop') return clear()
      setActors((prev) => (prev.some((a) => a.key === key) ? prev : [...prev, { key, name: e.actorName }]))
      const old = timers.current.get(key)
      if (old) clearTimeout(old)
      timers.current.set(key, setTimeout(clear, AUTO_CLEAR_MS))
    }
    socket.on('chat:channel:typing', onTyping)
    const live = timers.current
    return () => {
      socket.off('chat:channel:typing', onTyping)
      for (const t of live.values()) clearTimeout(t)
      live.clear()
    }
  }, [channel, chatId])

  return { actors }
}
